/**
 * The full Unicode emoji set for the sign-up position picker, from
 * emojibase-data. The table is ~560 KB of JSON, so it is a dynamic import: it
 * becomes its own chunk and is fetched only when a picker is first opened.
 *
 * Search runs over emojibase's English label, tags and shortcodes — the package
 * ships no Turkish locale.
 */
export interface PickerEmoji {
  char: string
  group: number
  /** Lower-cased label + tags + shortcodes, one string to `includes()` against. */
  search: string
}

/** emojibase's group index for skin tone / hair modifiers — not pickable alone. */
export const COMPONENT_GROUP = 2

interface CompactRow {
  unicode: string
  label: string
  group?: number
  order?: number
  tags?: string[]
  shortcodes?: string[]
}

let cache: Promise<PickerEmoji[]> | null = null

export function loadUnicodeEmojis(): Promise<PickerEmoji[]> {
  cache ??= import('emojibase-data/en/compact.json')
    .then(m => (m.default as unknown as CompactRow[])
      // No group = the bare regional indicator letters; they are flag halves.
      .filter(e => e.group !== undefined && e.group !== COMPONENT_GROUP)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(e => ({
        char: e.unicode,
        group: e.group!,
        search: [e.label, ...(e.tags ?? []), ...(e.shortcodes ?? [])].join(' ').toLowerCase(),
      })))
    .catch(err => { cache = null; throw err })   // a failed chunk load may retry
  return cache
}
